




















































import {
  Component,
  Prop,
  InjectReactive,
  Watch,
  Emit,
  mixins,
  Vue,
} from 'nuxt-property-decorator'
import mapboxgl, {
  MapboxGeoJSONFeature,
  MapMouseEvent,
  MapboxOptions,
} from 'mapbox-gl'
import MapPitchButtons from './MapPitchButtons.vue'
import MapLegends from '~/components/shared/map/MapLegends.vue'
import { getLotsStatus, getConsumerLot } from '~/api/consumer_map.api'
import { Amenity, Centerpoint, Lot, MapLegend } from '~/models'
import MapCardInfo from '~/components/consumer_map/MapCardInfo.vue'
import 'mapbox-gl/dist/mapbox-gl.css'
import { retrieveLegendsMap, shouldShowLegend } from '~/utils/map'
import { PublicProject } from '~/models/project'
import { LotTypes } from '~/enums/lots'
import globalStyling from '~/mixins/globalStyling'
import amenities from '~/mixins/amenities'
import seasons from '~/mixins/seasons'
import CategoriesFilter from '~/components/pois/CategoriesFilter.vue'
import { Events } from '~/utils/eventLayers'

@Component({
  components: {
    MapLegends,
    MapPitchButtons,
    CategoriesFilter,
  },
  mixins: [globalStyling, amenities, seasons],
})
export default class Map extends mixins(globalStyling, amenities, seasons) {
  @Prop({ default: (): Lot[] => [] })
  lots: Lot[]

  @Prop({ default: (): Amenity[] => [] })
  amenities: Amenity[]

  @Prop({ default: (): Amenity[] => [] })
  amenitiesData: Amenity[]

  @Prop({ default: (): Array<string> => [] })
  poisCategories: Array<string>

  @Prop({ default: null }) project: PublicProject
  @Prop({ default: {} }) filterParams: any
  @Prop({ default: false }) map3d: boolean
  @Prop({ default: (): Array<string> => [] }) lotsEdges: Array<string>
  @Prop({ default: true }) interactive: boolean
  @Prop({ default: 150 }) bottomPadding: number
  @Prop({ default: true }) summerSeason: boolean
  @Prop({ default: null }) selectedLot: string | null

  @InjectReactive() readonly loSidebarHover: any
  @InjectReactive() readonly queryFilters: string

  popup: any
  lotList: Lot[] = []
  currentParcelId: string | null
  map: mapboxgl.Map
  mapBoxClient: any
  mapBoxStyles: any
  legends: MapLegend[] = []
  activeProject: number
  feature: any
  lotsStatus: any = {}
  isSatelliteView: boolean = false
  isMobile: boolean = false
  unavailableLotTypes: Array<string> = [
    LotTypes.PRESALE,
    LotTypes.CONTRACT,
    LotTypes.CLOSED,
  ]

  timeout: any = undefined

  defaultConsumerZoom: number = 15
  defaultPitch: number = 0
  defaultBearing: number = 0

  mapOptions: MapboxOptions = {
    container: 'consumer_map',
    style: null,
    center: null,
    zoom: this.defaultConsumerZoom,
    antialias: true,
    doubleClickZoom: false,
  }

  navControl: any = {
    show: true,
    position: 'top-left',
  }

  dotColor: string = ''
  is3dMap: boolean = false
  selectedParcelId: string | null = null

  mounted() {
    this.init()
    window.addEventListener('resize', this.updateIsMobile)
  }

  destroyed() {
    window.removeEventListener('resize', this.updateIsMobile)
    this.$nuxt.$off('richTooltipPoi:onShowView')
    this.$nuxt.$off('resizeMap')
  }

  updateIsMobile() {
    this.isMobile = window.innerWidth < 1024
  }

  init() {
    const hasRequiredConfig: boolean =
      !!this.project &&
      !!this.project.config &&
      !!this.project.access_token &&
      !!this.project.mapbox_url

    if (!hasRequiredConfig) {
      return
    }

    this.lotList = [...this.lots]

    this.updateIsMobile()

    this.is3dMap =
      this.project.config?.pitch_desktop > 0 &&
      this.project?.config?.enable_3d_layers_consumer_map

    this.mapOptions.accessToken = this.project.access_token
    this.mapOptions.style = this.project.mapbox_url
    this.mapOptions.center = this.project.config.centerpoint_desktop

    this.mapOptions.pitch = this.map3d
      ? this.project.config?.pitch_desktop
      : this.defaultPitch

    this.mapOptions.bearing =
      this.project.config?.bearing_desktop || this.defaultBearing

    this.mapOptions.zoom =
      this.project?.config?.zoom_desktop || this.defaultConsumerZoom

    const mapConfig: any = {
      container: 'consumer_map',
      ...this.mapOptions,
      ...this.getAdvancedConfig(),
      center: {
        lng: this.mapOptions.center[0],
        lat: this.mapOptions.center[1],
      },
    }

    // @ts-ignore
    this.map = new mapboxgl.Map(mapConfig)
    if (this.interactive && !this.isMobile) {
      const nav = new mapboxgl.NavigationControl(this.navControl.options || {})
      this.map.addControl(nav, this.navControl.position)
    }

    this.map.on('load', () => {
      this.mapInit()
    })

    this.dotColor = this.getStyleVariableValue('--brand-primary')
    this.$nuxt.$on('richTooltipPoi:onShowView', (value: boolean) => {
      if (this.popup) {
        // this will center the rich tooltip full view when needed
        value
          ? this.popup.addClassName('center-popup-on-full-view')
          : this.popup.removeClassName('center-popup-on-full-view')
      }
    })

    this.$nuxt.$on('closePopup', () => {
      if (this.popup) {
        this.popup.remove()
        this.popup = null
      }
    })

    this.$nuxt.$on('resizeMap', this.resizeMap)
  }

  getAdvancedConfig(): any {
    const config: any = {
      interactive: this.interactive,
    }

    if (!this.isMobile) {
      config.cooperativeGestures = !!this.project.config?.enable_scroll_mode
      return config
    }

    config.pitch = this.map3d
      ? this.project.config?.pitch_mobile
      : this.defaultPitch

    config.center = this.project.config?.centerpoint_mobile
    config.bearing = this.project.config?.bearing_mobile || this.defaultBearing
    config.zoom = this.project?.config?.zoom_mobile || this.defaultConsumerZoom

    return config
  }

  async mapInit(): Promise<void> {
    if (!this.map) {
      return
    }

    if (this.seasonEnabled) {
      this.setupSeasons(this.map)
      this.switchSeason(this.summerSeason)
    }

    await this.initParcels()

    if (this.project.config.enable_pois_filtering && this.map)
      this.setUpAmenities(this.map, this.dotColor, this.amenities, this.project)
    this.onLotsEdgesChanged()

    this.setEvents(this.map)

    if (!this.interactive && this.map) {
      this.adjustMapBoundingBox()
      this.highlightLotsNoInteractiveMap()
    }

    this.getLegends()
  }

  @Emit('performSearch')
  performSearch(data: any): void {
    return data
  }

  @Watch('summerSeason')
  onSeasonChange(value: boolean): void {
    this.switchSeason(value)
  }

  mouseEnterAction(poi: any) {
    const mapOptions: {
      center?:
        | { lng: number; lat: number }
        | { lon: number; lat: number }
        | [number, number]
      essential: boolean
      speed: number
      zoom: number
    } = {
      essential: true,
      speed: 0.35,
      zoom: this.mapOptions.zoom,
    }

    mapOptions.center = [
      poi.address.geoCoordinates.longitude,
      poi.address.geoCoordinates.latitude,
    ]

    this.map.flyTo(mapOptions)
  }

  mouseLeaveAction(_poi: any) {
    // TODO: if we open the tooltip on hover then let's use this for remove it
  }

  getMapBoundingBox(lots: Array<Lot>) {
    const centerPoints = lots.map((lot) =>
      lot.centerpoint
        ? lot.centerpoint
        : lot.longitude && lot.latitude
        ? [lot.longitude, lot.latitude]
        : []
    )

    if (centerPoints.length > 0) {
      const southwesternCoordinate = [
        Math.min(...centerPoints.map((point: any) => point[0])),
        Math.min(...centerPoints.map((point: any) => point[1])),
      ]
      const northeasternCoordinate = [
        Math.max(...centerPoints.map((point: any) => point[0])),
        Math.max(...centerPoints.map((point: any) => point[1])),
      ]
      return [southwesternCoordinate, northeasternCoordinate]
    }
    return null
  }

  get hasFiltersApplied(): boolean {
    if (!this.filterParams) {
      return false
    }
    return !!Object.keys(this.filterParams).length
  }

  get isEdgeHighlightEnabled(): boolean {
    if (!this.project || !this.project.config) {
      return false
    }
    return this.project.config.enable_highlight_edges
  }

  @Watch('loSidebarHover')
  onloSidebarHoverChange(lightLot: any): void {
    if (!this.map) {
      return
    }

    if (lightLot) {
      const mapOptions: {
        center?:
          | { lng: number; lat: number }
          | { lon: number; lat: number }
          | [number, number]
        essential: boolean
      } = {
        essential: true,
      }

      const isLightLotInTheVisiblePartOfTheMap = this.map
        .getBounds()
        .contains(lightLot.centerpoint) // finds if a point is in the visible part of the map

      if (!isLightLotInTheVisiblePartOfTheMap) {
        mapOptions.center = lightLot.centerpoint
      }
      if (!this.isConsumerMapV3) {
        this.map.flyTo(mapOptions)
      }
      if (lightLot.isCondo) {
        const parcelIdParts = lightLot.parcel_id.split('_')
        lightLot.parcel_id = parcelIdParts[parcelIdParts.length - 1]
      }
      this.filterLotsOnMap('hover', '==', lightLot.parcel_id)
    } else {
      this.filterLotsOnMap('hover', '==', -1)
    }
  }

  adjustMapBoundingBox() {
    const boundingBox: any = this.getMapBoundingBox(this.lotList)

    const padding = {
      bottom: this.bottomPadding,
      top: 150,
      left: 150,
      right: 150,
    }

    if (!this.interactive) {
      padding.bottom = 50
      padding.top = 50
      padding.left = 50
      padding.right = 50
    }

    if (boundingBox) {
      this.map.fitBounds(boundingBox, {
        padding,
        maxZoom: 17,
      })
    }
  }

  @Watch('lots')
  onLotsChange(lots: Lot[]): void {
    this.lotList = Array.from(new Set([...lots, ...this.lotList]))
    if (!this.interactive && this.map) {
      this.adjustMapBoundingBox()
    }
  }

  highlightLotsNoInteractiveMap() {
    const lotToHighligh = this.lots ? this.lots.map((lot) => lot.parcel_id) : []
    if (lotToHighligh.length > 0) {
      this.filterLotsOnMap('search_results', 'in', lotToHighligh)
    }
  }

  @Watch('filterParams')
  onfilterParamsChange(filterParams: any): void {
    this.filterLotsOnMap('tab_outline', 'in', [])
    this.getLegends()

    if (!this.isEdgeHighlightEnabled) {
      return
    }

    if (!this.interactive) {
      this.highlightLotsNoInteractiveMap()
      return
    }
    this.getFilteredLots(filterParams)
  }

  getFilteredLots(filterParams: any): void {
    getLotsStatus(this.$route.params.slug, filterParams).then((parcelIds) => {
      this.filterLotsOnMap(
        'search_results',
        'in',
        this.hasFiltersApplied ? parcelIds.available : []
      )
    })
  }

  @Watch('lotsEdges')
  onLotsEdgesChanged(): void {
    if (!this.map) {
      return
    }

    if (!this.lotsEdges?.length) {
      return
    }

    this.filterLotsOnMap('tab_outline', 'in', [])
    this.filterLotsOnMap('search_results', 'in', [])

    const layer: string = this.hasFiltersApplied
      ? 'search_results'
      : 'tab_outline'

    this.filterLotsOnMap(layer, 'in', this.lotsEdges)
  }

  async getLotFromAPI(parcel_id: string): Promise<Lot> {
    const lots = await getConsumerLot(parcel_id)
    return lots
  }

  toggleSatelliteView(): void {
    if (!this.map) return
    this.isSatelliteView = !this.isSatelliteView

    this.map.setLayoutProperty(
      'mapbox-satellite',
      'visibility',
      this.isSatelliteView ? 'visible' : 'none'
    )
  }

  flyToCenterpoint(): void {
    if (!this.map) return

    const mapOptions: {
      center?:
        | { lng: number; lat: number }
        | { lon: number; lat: number }
        | [number, number]
      essential: boolean
    } = {
      essential: true,
    }
    mapOptions.center = this.project.config?.centerpoint_desktop
    this.map.flyTo(mapOptions)
  }

  getLegends(): void {
    let availableLayers: Array<any> = this.project.theme.layers.map(
      (layer: any) => ({
        ...layer,
        status: layer.key,
        show:
          shouldShowLegend(
            layer.key,
            this.lotsStatus[layer.key] ? this.lotsStatus[layer.key].length : 1
          ) && layer.key !== 'search_results',
      })
    )

    const searchResultsLayer = this.map.getLayer('search_results')
    if (this.hasFiltersApplied && searchResultsLayer) {
      availableLayers = availableLayers.map((layer: any) => {
        if (layer.key !== 'search_results') {
          return layer
        }

        return {
          ...layer,
          show: true,
        }
      })
    }

    this.legends = retrieveLegendsMap(this.map, availableLayers)
    this.legends.forEach((layer: any) => {
      if (layer.show) {
        this.setLayer(layer, 'features')
      }
    })
  }

  setLayer(
    layer: { color: string; key: string; label: string },
    modelLayer: string
  ) {
    if (!this.map) {
      return
    }

    const fillColor = layer.color
    const layerId = layer.key
    if (!layerId) {
      return
    }
    if (!this.map.getLayer(layerId)) {
      const { source, sourceLayer } = this.map.getLayer(modelLayer) as any

      this.map.addLayer({
        id: layerId,
        type: 'fill',
        source, // reference the data source
        'source-layer': sourceLayer, // reference the data source
        layout: {},
        paint: {
          'fill-color': fillColor, // color fill
          'fill-opacity': 0.7,
        },
      })
      this.placeLayerOver(layerId, modelLayer)
    } else {
      this.map.setPaintProperty(layerId, 'fill-color', fillColor)
    }
  }

  placeLayerOver(layerName: string, positioningLayer: string) {
    // @ts-ignore
    const currentLayerOrder = this.map.style.order
    const positioningLayerIndex = currentLayerOrder.indexOf(positioningLayer)
    if (positioningLayerIndex !== -1) {
      this.map.moveLayer(
        layerName,
        currentLayerOrder[positioningLayerIndex + 1]
      )
    }
  }

  @Watch('amenities', { deep: true })
  onAmenitiesChanged() {
    this.updateAmenities()
  }

  get seasonEnabled() {
    return !!this.project?.config?.enable_season
  }

  setEvents(map: any) {
    map.off('mousemove', this.debounceLotHover)
    map.off('mouseleave', this.onMapLotBlur)
    map.off('click', this.onMapLotClickDesktop)

    const features = this.is3dMap ? '3d-features' : 'features'
    map.on(
      'mousemove',
      features,
      (
        ev: MapMouseEvent & {
          features?: MapboxGeoJSONFeature[] | undefined
        }
      ) => {
        this.debounceLotHover(ev, 60)
      }
    )

    map.on('mouseleave', features, this.onMapLotBlur)
    if (this.isMobile) {
      map.on('click', this.onMapLotClickMobile)
    } else {
      map.on('click', features, this.onMapLotClickDesktop)
    }
  }

  async initParcels(): Promise<void> {
    try {
      if (!this.map) {
        return
      }

      this.lotsStatus = await getLotsStatus(this.$route.params.slug)
      if (this.hasFiltersApplied) {
        this.getFilteredLots(this.filterParams)
      } else {
        this.filterLotsOnMap('search_results', 'in', [])
      }

      Object.keys(this.lotsStatus).forEach((key) => {
        if (this.lotsStatus[key]?.length > 0 && this.map.getLayer(key)) {
          this.filterLotsOnMap(key, 'in', this.lotsStatus[key])
        }
      })
    } catch (exception) {
      throw new Error(exception)
    }
  }

  debounceLotHover(event: any, _timeout: number): void {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => {
      this.onMapLotHover(event)
    }, _timeout)
  }

  async onMapLotHover(event: any): Promise<void> {
    if (!this.map) {
      return
    }

    const feature: any = this.getMapFeature(event)
    if (!feature || this.feature === feature) {
      return
    }

    this.feature = feature
    const parcel_id = feature?.properties?.parcel_id
    if (parcel_id && this.currentParcelId !== parcel_id) {
      this.currentParcelId = parcel_id

      const listElement = this.getLotFromList(parcel_id)
      if (!listElement) {
        this.map.getCanvas().style.cursor = 'wait'
        const lot: Lot = await this.getLotFromAPI(parcel_id)

        if (!lot.available) {
          this.map.getCanvas().style.cursor = 'not-allowed'
          return
        }

        const unavailableType: string = this.unavailableLotTypes.find(
          (type: string) => type === lot.type
        )

        if (unavailableType) {
          this.map.getCanvas().style.cursor = 'not-allowed'
          return
        }

        if (!lot) {
          this.map.getCanvas().style.cursor = 'not-allowed'
        } else {
          this.map.getCanvas().style.cursor = 'pointer'
          this.setTooltipOnMap(lot)
          this.lotList.push(lot)
        }
      } else {
        this.map.getCanvas().style.cursor = 'pointer'
        this.setTooltipOnMap(listElement)
      }

      const layer = this.is3dMap ? '3d-hover' : 'hover'
      this.map?.setFilter(layer, ['==', ['get', 'parcel_id'], parcel_id])
    }
  }

  async onMapLotClickDesktop(event: MapMouseEvent): Promise<void> {
    const feature: any = this.getMapFeature(event)
    if (feature) {
      const parcelId = feature?.properties?.parcel_id
      if (!parcelId) {
        return
      }
      const lot: Lot = await this.getLotFromAPI(parcelId)
      if (!lot.parcel_id) {
        return
      }
      if (!lot.available) {
        this.emitNullLot()
        return
      }
      const unavailableType: string = this.unavailableLotTypes.find(
        (type: string) => type === lot.type
      )
      if (unavailableType) {
        this.emitNullLot()
        return
      }
      this.$emit('setLot', lot)
      this.sendLotMapClickToGTM(lot)
    }
  }

  async onMapLotClickMobile(event: MapMouseEvent): Promise<void> {
    const feature: any = this.getMapFeature(event)
    if (feature) {
      const parcelId = feature?.properties?.parcel_id
      if (!parcelId) {
        this.emitNullLot()
        return
      }
      const lot: Lot = await this.getLotFromAPI(parcelId)
      if (!lot) {
        this.emitNullLot()
        return
      }
      if (!lot.available) {
        this.emitNullLot()
        return
      }
      const unavailableType: string = this.unavailableLotTypes.find(
        (type: string) => type === lot.type
      )
      if (unavailableType) {
        this.emitNullLot()
        return
      }
      this.lotList.push(lot)

      this.$emit('setLot', lot)
      this.sendLotMapClickToGTM(lot)
    } else {
      this.emitNullLot()
    }
  }

  sendLotMapClickToGTM(lot: any): void {
    Events.lot('lotClick', 'click | map view', lot, 'map')
  }

  emitNullLot(): void {
    this.$emit('setLot', null)
  }

  getMapFeature(event: any): Array<any> {
    const feature = this.getFeatureFromEvent(event.point)
    return feature
  }

  getFeatureFromEvent(point: any): any {
    const featuresSelected = this.is3dMap ? '3d-features' : 'features'
    const features = this.map.queryRenderedFeatures(point, {
      layers: [featuresSelected],
    })
    return features[0]
  }

  getLotFromList(parcel_id: any): Lot {
    const lot = this.lotList.find((lot) => {
      return lot.parcel_id === parcel_id || lot.parcel_id.endsWith(parcel_id)
    })
    return lot
  }

  setTooltipOnMap(lot: Lot): void {
    if (this.isMobile) return
    if (this.popup?.isRichPopup) return

    if (lot) {
      if (this.popup) {
        this.popup.remove()
        this.popup = null
      }

      // @ts-ignore
      const centerpoint: any[] = this.is3dMap
        ? this.getLngLatAlt(lot)
        : this.getLngLat(lot)
      this.popup = new mapboxgl.Popup({
        className: 'tooltip',
        closeButton: false,
        closeOnClick: false,
        maxWidth: '100%',
        offset: centerpoint.length === 3 ? centerpoint[2] : 0,
      })

      this.popup.setLngLat(centerpoint)
      this.popup.setHTML(
        '<div id="vue-popup-content" style="background-color: blue" class="invisible md:visible"></div>'
      )
      this.popup.addTo(this.map)

      const tooltip = new MapCardInfo({
        parent: new Vue({
          store: this.$store,
        }),
        propsData: {
          lotInfo: lot,
          project: this.project,
        },
      })
      tooltip.$mount('#vue-popup-content')
      tooltip.$on('tooltipMouseLeave', this.onMapLotBlur)
    }
  }

  getLngLat(lot: Lot): Centerpoint {
    return lot.centerpoint
  }

  getLngLatAlt(lot: Lot): number[] {
    if (!lot.centerpoint_3d) {
      return []
    }
    return lot.centerpoint_3d
  }

  onMapLotBlur(event?: any): void {
    if (event) {
      const feature: any = this.getMapFeature(event)
      if (feature?.properties?.parcel_id === this.currentParcelId) {
        return
      }
    }
    const layer = this.is3dMap ? '3d-hover' : 'hover'

    if (this.map) {
      this.map.getCanvas().style.cursor = ''
      this.map?.setFilter(layer, [
        '==',
        ['get', 'parcel_id'],
        !this.selectedParcelId ? -1 : this.selectedParcelId,
      ])
    }

    this.currentParcelId = null

    clearTimeout(this.timeout)
    this.timeout = null
    if (this.popup && !this.popup?.isRichPopup) {
      this.popup.remove()
      this.popup = null
    }
  }

  onPitchClicked(pitch: number): void {
    if (this.map) {
      this.is3dMap = pitch > 0
      if (!this.project.config?.enable_3d_layers_consumer_map) {
        this.is3dMap = false
      }
      this.map.setPitch(pitch)
      this.setEvents(this.map)
    }
  }

  @Emit('setLotsCountFilters')
  setLotsCountFilter(
    totalLots: number,
    filteredLotsCount: number
  ): { totalLots: number; filteredLotsCount: number } {
    return { totalLots, filteredLotsCount }
  }

  filterLotsOnMap(
    layer: string,
    condition: string,
    items: Array<string> | number
  ): void {
    const _layer = this.is3dMap ? `3d-${layer}` : layer
    const mapLayer: any = this.map?.getLayer(_layer)
    if (!mapLayer) {
      return
    }

    this.map.setFilter(_layer, [
      condition,
      ['get', 'parcel_id'],
      ['literal', items],
    ])
  }

  get isConsumerMapV3(): boolean {
    // this will help to skip the flying movements in the map when the user hovers over a card. Is not required for V3
    return this.$route.name.includes('v3-map-slug')
  }

  @Watch('selectedLot')
  setParcelIdSelected(lot: Lot): void {
    if (lot) {
      this.selectedParcelId = lot.parcel_id
    } else {
      this.selectedParcelId = null
      // setting this filter here because the map v2 does not trigger onMapLotBlur function
      this.map?.setFilter('hover', ['==', ['get', 'parcel_id'], -1])
    }
  }

  resizeMap(): void {
    if (this.map) {
      this.map.resize()
    }
  }
}
